import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { normalizePhoneOrEmail } from "@/lib/phone";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(normalizePhoneOrEmail(identifier), password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
      if (u.role === "admin") {
        nav("/admin");
      } else if (u.status === "otp_pending") {
        nav("/verify-otp");
      } else {
        nav("/catalog");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div
        className="hidden lg:block relative"
        style={{
          backgroundImage: "url('https://images.pexels.com/photos/32315685/pexels-photo-32315685.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-[#D62828]/30" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="tracking-luxe text-[10px] text-[#F4A100] mb-3">Shubh Vivah</div>
          <p className="font-serif text-3xl leading-tight max-w-md">A private atelier portal for our most discerning trade partners.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16 bg-[#FAF9F6]">
        <form onSubmit={onSubmit} className="w-full max-w-md sv-anim-in" data-testid="login-form">
          <div className="tracking-luxe text-[10px] text-[#F4A100] mb-3">Welcome back</div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#D62828] mb-2">Sign in</h1>
          <p className="text-[#4A4A4A] mb-10">with your registered WhatsApp number</p>

          <label className="block tracking-luxe text-[10px] text-[#4A4A4A] mb-2">Phone number</label>
          <input
            type="tel"
            required
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            data-testid="login-identifier"
            placeholder="9876543210"
            autoComplete="username"
            className="w-full bg-transparent border-b border-[#E5E0D8] focus:border-[#F4A100] outline-none py-3 mb-2 text-[#1A1A1A]"
          />
          <p className="text-[10px] text-[#4A4A4A] mb-6">Type your 10-digit number — we add +91 automatically.</p>

          <label className="block tracking-luxe text-[10px] text-[#4A4A4A] mb-2">Password</label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password"
            autoComplete="current-password"
            className="w-full bg-transparent border-b border-[#E5E0D8] focus:border-[#F4A100] outline-none py-3 mb-3 text-[#1A1A1A]"
          />
          <p className="text-xs text-[#4A4A4A] mb-3">Atelier owner: use your WhatsApp number <span className="text-[#D62828]">+91 9573311919</span> above.</p>
          <p className="text-sm text-[#4A4A4A] mb-7">
            <Link to="/forgot-password" data-testid="link-to-forgot" className="text-[#D62828] underline underline-offset-4 decoration-[#F4A100]">Forgot your password?</Link>
          </p>

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full bg-[#D62828] hover:bg-[#A41C1C] disabled:opacity-60 text-white py-4 tracking-luxe text-xs transition-colors"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>

          <p className="text-sm text-[#4A4A4A] mt-8 text-center">
            New here?{" "}
            <Link to="/register" className="text-[#D62828] underline underline-offset-4 decoration-[#F4A100]" data-testid="link-to-register">
              Create an account
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
